import { EmptyState } from '@agentic-flow-studio/shared/components/empty-state';
import { JobsTable } from '@agentic-flow-studio/shared/components/job-plan/JobsTable';
import { trpc } from '@agentic-flow-studio/shared/lib/trpc';
import type { JobStatus } from '@agentic-flow-studio/shared/types/job-plan';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@agentic-flow-studio/shared/ui/alert-dialog';
import { Badge } from '@agentic-flow-studio/shared/ui/badge';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@agentic-flow-studio/shared/ui/dialog';
import { Input } from '@agentic-flow-studio/shared/ui/input';
import {
  Calendar,
  CalendarClock,
  Filter,
  Loader2,
  Plus,
  Search,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { JobPlanCopilotPanel } from '../components/chat/JobPlanCopilotPanel';

export default function JobsPage() {
  const { orgId, workspaceId } = useParams<{ orgId: string; workspaceId: string }>();
  const navigate = useNavigate();
  const basePath = `/org/${orgId}/workspace/${workspaceId}/jobs`;

  const today = new Date().toISOString().split('T')[0];
  const [serviceDate, setServiceDate] = useState<string>(today);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isCopilotOpen, setIsCopilotOpen] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isReschedulingOpen, setIsReschedulingOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(today);

  const openIngest = () => {
    navigate(`/org/${orgId}/workspace/${workspaceId}/schedule/ingest?serviceDate=${serviceDate}`);
  };

  const { data: jobs = [], isLoading } = trpc.jobPlan.jobs.list.useQuery({
    serviceDate: serviceDate || undefined,
    status: statusFilter !== 'all' ? (statusFilter as JobStatus) : undefined,
  });

  const { data: pendingProposals = [] } = trpc.jobPlan.listProposals.useQuery();

  const utils = trpc.useUtils();
  const deleteManyMutation = trpc.jobPlan.jobs.deleteMany.useMutation({
    onSuccess: ({ deletedCount }) => {
      toast.success(`Deleted ${deletedCount} job${deletedCount === 1 ? '' : 's'}`);
      utils.jobPlan.jobs.list.invalidate();
      setSelectedIds(new Set());
      setIsConfirmingDelete(false);
    },
    onError: (error) => {
      toast.error(`Failed to delete jobs: ${error.message}`);
      setIsConfirmingDelete(false);
    },
  });

  const rescheduleManyMutation = trpc.jobPlan.jobs.rescheduleMany.useMutation({
    onSuccess: ({ rescheduledCount }) => {
      toast.success(
        `Rescheduled ${rescheduledCount} job${rescheduledCount === 1 ? '' : 's'} to ${rescheduleDate}`
      );
      utils.jobPlan.jobs.list.invalidate();
      setSelectedIds(new Set());
      setIsReschedulingOpen(false);
    },
    onError: (error) => {
      toast.error(`Failed to reschedule jobs: ${error.message}`);
      setIsReschedulingOpen(false);
    },
  });

  const filteredJobs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return jobs;
    return jobs.filter((job) => {
      const haystack = [job.title, job.formattedAddress, job.contact?.name, job.contact?.company]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [jobs, searchQuery]);

  // Reset selection whenever the underlying dataset changes (date/status filter) so
  // stale ids from a previous view can't be bulk-deleted invisibly.
  useEffect(() => {
    setSelectedIds(new Set());
  }, []);

  const toggleSelected = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) =>
      prev.size === filteredJobs.length ? new Set() : new Set(filteredJobs.map((job) => job.id))
    );
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background">
      {/* Top action bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-4 border-b border-border/40 bg-card">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-bold tracking-tight">Jobs</h1>
          <div className="flex items-center gap-1.5">
            <Input
              type="date"
              value={serviceDate}
              onChange={(e) => setServiceDate(e.target.value)}
              className="w-36 h-8 text-xs"
            />
            {serviceDate ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setServiceDate('')}
                className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
                title="Show all dates"
              >
                All Dates
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setServiceDate(today)}
                className="h-8 px-2 text-xs"
              >
                Today
              </Button>
            )}
          </div>

          <Badge variant="outline" className="text-xs">
            {filteredJobs.length} Jobs
          </Badge>
        </div>

        <div className="flex items-center gap-2">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search jobs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-48 h-8 pl-8 text-xs"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-1 bg-muted/40 p-1 rounded-lg border border-border/50 text-xs">
            <Filter className="h-3.5 w-3.5 text-muted-foreground ml-1 mr-0.5" />
            {(['all', 'unassigned', 'assigned', 'in_progress', 'completed'] as const).map((st) => (
              <button
                key={st}
                type="button"
                className={`px-2 py-1 rounded text-xs capitalize transition-colors ${
                  statusFilter === st
                    ? 'bg-background font-semibold text-foreground shadow-xs'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setStatusFilter(st)}
              >
                {st.replace('_', ' ')}
              </button>
            ))}
          </div>

          <Button
            size="sm"
            variant="outline"
            onClick={openIngest}
            className="gap-1.5 border-primary/40 text-primary hover:bg-primary/10"
          >
            <Sparkles className="h-3.5 w-3.5" />
            <span>AI Ingest</span>
            {pendingProposals.length > 0 && (
              <span className="ml-0.5 px-1.5 py-0.2 bg-primary text-primary-foreground text-[10px] rounded-full font-bold">
                {pendingProposals.length}
              </span>
            )}
          </Button>

          <Button
            variant={isCopilotOpen ? 'default' : 'outline'}
            size="sm"
            className="gap-1.5"
            onClick={() => setIsCopilotOpen(!isCopilotOpen)}
            title="Toggle AI Job Plan Copilot"
          >
            <Sparkles className="h-3.5 w-3.5" />
            <span>AI Copilot</span>
          </Button>

          <Button size="sm" asChild className="gap-1.5">
            <Link to={`${basePath}/new`}>
              <Plus className="h-4 w-4" />
              <span>Create Job</span>
            </Link>
          </Button>
        </div>
      </div>

      {/* Main Jobs List & Copilot Split */}
      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 overflow-y-auto p-4">
          {!isLoading && filteredJobs.length === 0 ? (
            <EmptyState
              icon={Calendar}
              title={searchQuery ? 'No jobs match your search' : `No jobs found for ${serviceDate}`}
              description={
                !searchQuery
                  ? 'Create your first job for this service date or import using AI Ingest.'
                  : 'Try relaxing your search terms or checking another service date.'
              }
              action={
                !searchQuery ? (
                  <>
                    <Button size="sm" variant="outline" onClick={openIngest} className="gap-1.5">
                      <Sparkles className="h-3.5 w-3.5 text-primary" />
                      <span>AI Ingest</span>
                    </Button>
                    <Button size="sm" asChild>
                      <Link to={`${basePath}/new`}>
                        <Plus className="h-3.5 w-3.5 mr-1" />
                        <span>Create Job</span>
                      </Link>
                    </Button>
                  </>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setSearchQuery('')}>
                    Clear search
                  </Button>
                )
              }
            />
          ) : (
            <div className="flex flex-col gap-2">
              {selectedIds.size > 0 && (
                <div className="flex items-center justify-between px-3 py-2 rounded-lg border border-border/60 bg-muted/40">
                  <span className="text-xs font-medium text-foreground">
                    {selectedIds.size} selected
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs gap-1 text-muted-foreground"
                      onClick={() => setSelectedIds(new Set())}
                    >
                      <X className="h-3 w-3" />
                      Clear
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs gap-1.5"
                      onClick={() => {
                        setRescheduleDate(today);
                        setIsReschedulingOpen(true);
                      }}
                    >
                      <CalendarClock className="h-3.5 w-3.5" />
                      Reschedule
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      className="h-7 text-xs gap-1.5"
                      onClick={() => setIsConfirmingDelete(true)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete
                    </Button>
                  </div>
                </div>
              )}

              <div className="rounded-xl border border-border/60 bg-card shadow-sm overflow-hidden">
                <JobsTable
                  items={filteredJobs}
                  isLoading={isLoading}
                  onRowClick={(id) => navigate(`${basePath}/${id}`)}
                  showServiceDate={!serviceDate}
                  emptyLabel="No jobs found"
                  selectedIds={selectedIds}
                  onToggleSelected={toggleSelected}
                  onToggleSelectAll={toggleSelectAll}
                />
              </div>
            </div>
          )}
        </div>

        {/* AI Job Plan Copilot on Far Right */}
        <JobPlanCopilotPanel
          serviceDate={serviceDate}
          isOpen={isCopilotOpen}
          onToggle={() => setIsCopilotOpen(!isCopilotOpen)}
          onOpenIngestDrawer={openIngest}
        />
      </div>

      <AlertDialog open={isConfirmingDelete} onOpenChange={setIsConfirmingDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} job(s)?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the selected job{selectedIds.size === 1 ? '' : 's'} and
              any scheduled route stops. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteManyMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 gap-1.5"
              disabled={deleteManyMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                deleteManyMutation.mutate({ ids: Array.from(selectedIds) });
              }}
            >
              {deleteManyMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={isReschedulingOpen} onOpenChange={setIsReschedulingOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reschedule {selectedIds.size} job(s)</DialogTitle>
            <DialogDescription>
              Move the selected job{selectedIds.size === 1 ? '' : 's'} to a new service date. Any of
              them already assigned to a route on their old date will be unassigned.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={rescheduleDate}
              onChange={(e) => setRescheduleDate(e.target.value)}
              className="w-40 h-9 text-sm"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setRescheduleDate(today)}
              disabled={rescheduleDate === today}
            >
              Today
            </Button>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsReschedulingOpen(false)}
              disabled={rescheduleManyMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              className="gap-1.5"
              disabled={!rescheduleDate || rescheduleManyMutation.isPending}
              onClick={() =>
                rescheduleManyMutation.mutate({
                  ids: Array.from(selectedIds),
                  newServiceDate: rescheduleDate,
                })
              }
            >
              {rescheduleManyMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Move to {rescheduleDate || '...'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
